import { MetaRating } from "../../api/privateRequests/squad-builder/get-meta-ratings";
import {
  RangeFilterType,
  SelectorFilterType,
  SelectorOption,
} from "../../components/organisms/Filters/PlayerFilters/PlayerFilters";
import {
  ChemPlayer,
  ChemistryProfiles,
  PlayersChemistry,
  calculateChemistry,
} from "../../functions/calculateChemistry";
import getFormation from "../../functions/getFormation";
import { Tactic } from "../tactics/tacticsSlice";

export type Manager = {
  country?: number;
  league?: number;
};

export type Scope = "market" | "both" | "club";
export type Metal = "bronze" | "silver" | "gold";
export const numberToMetal = (index: number): Metal => {
  switch (index) {
    case 0:
      return "gold";
    case 1:
      return "silver";
    case 2:
      return "bronze";
    default:
      return undefined;
  }
};

export type Playstyle = {
  id: number;
  label: string;
  category: string;
};

export type Squad = {
  players: SquadBuilderPlayerType[];
  manager: Manager;
  chemistry: ChemistryOverview;
  suggestedSquadChemistry: number;
  metaRating: number;
};

export type RatingFilter = {
  min?: number;
  max?: number;
};

export const emptyPlayer = {} as SquadBuilderPlayerType;
export const setEmptySquad = (
  playerRoles?: { playerRoleId: number }[],
  chosenIngameFormation: number = 0,
) => ({
  chosenIngameFormation,
  players: (playerRoles as SquadBuilderPlayerType[]) ?? [
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
  ],
  manager: {
    country: undefined,
    league: undefined,
  },
  chemistry: {
    teamChemistry: 0,
    playersOutOfPosition: [],
    playersChemistry: {} as PlayersChemistry,
    summary: {
      countryCounter: {},
      leagueCounter: {},
      clubCounter: {},
    },
  },
  suggestedSquadChemistry: 0,
  metaRating: 0,
  ingameFormations: [],
});

export type SquadBuilderPlayerType = {
  resourceId: number;
  assetId?: number;
  eaPlayerId?: number;
  clubId: number;
  leagueId: number;
  countryId: number;
  rating: number;
  price: number;
  playerRoleId: number;
  preferredPosition: string;
  possiblePositions: string[];
  owned: boolean;
  cardName: string;
  untradeable: boolean;
  versionId: number;
  metalId: number;
  attributes: number[];
  rolesPlus: number[];
  chemIndex?: number;
  playStylesPlus: number[];
  position?: string;
  userOwned?: boolean;
  isAIPlayer?: boolean;
  metaRatings: MetaRating[];
  metaRating?: number;
  hasDynamicImage: boolean;
  playerUrl: string;
  sbcPrice: number;
  isObjectivePlayer: boolean;
  isSbcPlayer: boolean;
  canEvolve: boolean;
  isLoanPlayer?: boolean;
  numberOfLoanedGames?: number;
  evolutionPath?: number[];
  evoPathId?: number;
  chemistry?: number;
  chemstyleId?: number;
  teamMetaRating?: number;
  teamChemistry?: number;
};

export const chemStylesToIcon = {
  250: "\uE033",
  251: "\uE044",
  252: "\uE038",
  253: "\uE036",
  254: "\uE03F",
  255: "\uE03C",
  256: "\uE031",
  257: "\uE030",
  258: "\uE040",
  259: "\uE03E",
  260: "\uE037",
  261: "\uE041",
  262: "\uE03B",
  263: "\uE039",
  264: "\uE032",
  265: "\uE02F",
  266: "\uE03D",
  267: "\uE035",
  268: "\uE042",
  269: "\uE045",
  270: "\uE043",
  271: "\uE034",
  272: "\uE03A",
  273: "\uE033",
};

export type PlayerToInclude = {
  playerRoleId: number;
  player?: SquadBuilderPlayerType;
  userAddedPlayerOnRole?: boolean;
};

export type FromBackendSquadBuilderPlayer = {
  resourceId: number;
  canEvolve: boolean;
  eaPlayerId?: number;
  chemIndex: number;
  inGameIndex: number;
  cardName: string;
  countryId: number;
  leagueId: number;
  clubId: number;
  rating: number;
  metalId: number;
  versionId: number;
  attributes: number[];
  isSbcPlayer: boolean;
  isObjectivePlayer: boolean;
  price: number;
  preferredPosition: string;
  possiblePositions: string[];
  untradeable: boolean;
  owned: boolean;
  assetId: number;
  hasDynamicImage: boolean;
  playerUrl: string;
  metaRatings: MetaRating[];
  sbcPrice: number;
  playStylesPlus: number[];
};

export type ChemSummary = {
  countryCounter: { [key: string]: number };
  leagueCounter: { [key: string]: number };
  clubCounter: { [key: string]: number };
};

export type ChemistryOverview = {
  teamChemistry: number;
  playersOutOfPosition: ChemPlayer[];
  playersChemistry: PlayersChemistry;
  summary: ChemSummary;
};

export const getTeamMetaRating = ({
  playersChemistry,
  players,
}: {
  playersChemistry: PlayersChemistry;
  players: SquadBuilderPlayerType[];
}) => {
  let metaRatingSum = 0;
  for (let i = 0; i < 11; i++) {
    const player = players[i];

    metaRatingSum +=
      player?.metaRatings?.[playersChemistry[i]]?.metaRating || 0;
  }
  return metaRatingSum >= 0 ? metaRatingSum / 11 : 0;
};

export const getChemistry = ({
  players,
  manager,
  formation,
  chemistryProfiles,
}: {
  players: ChemPlayer[];
  manager: Manager;
  formation: string;
  chemistryProfiles: ChemistryProfiles;
}): ChemistryOverview => {
  const chemPlayers = [];
  for (let i = 0; i < 11; i++) {
    const p = players[i];
    if (p?.possiblePositions.length > 0)
      chemPlayers.push({
        clubId: p.clubId,
        leagueId: p.leagueId,
        countryId: p.countryId,
        positionIndex: i,
        possiblePositions: p.possiblePositions,
        versionId: p.versionId,
      });
  }
  const chemistryObject = calculateChemistry({
    players: chemPlayers,
    formation: getFormation(formation).labels,
    manager,
    chemistryProfiles,
  });
  const teamChemistry = Object.values(chemistryObject.playersChemistry).reduce(
    (sum, chemistry) => sum + chemistry,
    0,
  );

  return {
    teamChemistry,
    playersOutOfPosition: chemistryObject.playersOutOfPosition,
    playersChemistry: chemistryObject.playersChemistry,
    summary: {
      countryCounter: chemistryObject.countryCounter,
      leagueCounter: chemistryObject.leagueCounter,
      clubCounter: chemistryObject.clubCounter,
    },
  };
};

export type SquadBuilderMode = "default" | "best-formation";
export type RightPanelMode =
  | "default"
  | "add-player"
  | "add-player-filters"
  | "add-player-role"
  | "add-evo-player"
  | "suggested-squads"
  | "squad-overview"
  | "select-chem-style"
  | "add-more-filters";

export type LeftPanelMode = "chem-overview" | "choose-formation" | "tactics";

export interface ChoiceFilter<T extends String | Number> {
  values: T[];
}

export type MinMaxFilter = {
  min?: number;
  max?: number;
};

export type OtherAttributes = {
  skillMoves?: ChoiceFilter<number>;
  weakFoot?: ChoiceFilter<number>;
  preferredFoot?: ChoiceFilter<string>;
  height?: MinMaxFilter;
};

type InGameAttributes = {
  acceleration?: MinMaxFilter;
  sprintSpeed?: MinMaxFilter;
  agility?: MinMaxFilter;
  balance?: MinMaxFilter;
  jumping?: MinMaxFilter;
  stamina?: MinMaxFilter;
  strength?: MinMaxFilter;
  reactions?: MinMaxFilter;
  aggression?: MinMaxFilter;
  composure?: MinMaxFilter;
  interceptions?: MinMaxFilter;
  positioning?: MinMaxFilter;
  vision?: MinMaxFilter;
  ballControl?: MinMaxFilter;
  crossing?: MinMaxFilter;
  dribbling?: MinMaxFilter;
  finishing?: MinMaxFilter;
  freeKickAccuracy?: MinMaxFilter;
  headingAccuracy?: MinMaxFilter;
  longPassing?: MinMaxFilter;
  shortPassing?: MinMaxFilter;
  defensiveAwareness?: MinMaxFilter;
  shotPower?: MinMaxFilter;
  longShots?: MinMaxFilter;
  standingTackles?: MinMaxFilter;
  slidingTackles?: MinMaxFilter;
  volleys?: MinMaxFilter;
  curve?: MinMaxFilter;
  penalties?: MinMaxFilter;
  gkDiving?: MinMaxFilter;
  gkHandling?: MinMaxFilter;
  gkKicking?: MinMaxFilter;
  gkReflexes?: MinMaxFilter;
  gkPositioning?: MinMaxFilter;
  traits?: MinMaxFilter;
  specialties?: MinMaxFilter;
};

export const setEmptyInGameAttributes = (): InGameAttributes =>
  Object.keys(inGameAttributesDict).reduce((a, v) => ({ ...a, [v]: {} }), {});

export const setEmptyOtherAttributes = (): OtherAttributes =>
  Object.keys(otherAttributeSettings).reduce((a, v) => ({ ...a, [v]: [] }), {});

export const setEmptyAttributes = ({
  playStylesFilterOptions,
  playStylesPlusFilterOptions,
}: {
  playStylesFilterOptions?: SelectorOption[];
  playStylesPlusFilterOptions?: SelectorOption[];
}) => [
  {
    label: "PlayStyles",
    key: "playStyles",
    type: "SELECTOR_FILTER",
    options: playStylesFilterOptions || [],
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "Playstyle+",
    key: "playStylesPlus",
    type: "SELECTOR_FILTER",
    options: playStylesPlusFilterOptions || [],
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "Skill Moves",
    shortLabel: "SM",
    key: "skillMoves",
    type: "SELECTOR_FILTER",
    options: [
      { id: 1, label: "1", category: "Skill Moves" },
      { id: 2, label: "2", category: "Skill Moves" },
      { id: 3, label: "3", category: "Skill Moves" },
      { id: 4, label: "4", category: "Skill Moves" },
      { id: 5, label: "5", category: "Skill Moves" },
    ],
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "Weak Foot",
    shortLabel: "WF",
    key: "weakFoot",
    type: "SELECTOR_FILTER",
    options: [
      { id: 1, label: "1", category: "Weak Foot" },
      { id: 2, label: "2", category: "Weak Foot" },
      { id: 3, label: "3", category: "Weak Foot" },
      { id: 4, label: "4", category: "Weak Foot" },
      { id: 5, label: "5", category: "Weak Foot" },
    ],
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "Preferred Foot",
    shortLabel: "PF",
    key: "preferredFoot",
    type: "SELECTOR_FILTER",
    useLabelAsId: true,
    options: [
      { id: 0, label: "Left", category: "Preferred Foot" },
      { id: 1, label: "Right", category: "Preferred Foot" },
    ],
    selectedOptions: [],
  } as SelectorFilterType,
  ...Object.entries(inGameAttributesDict).map(
    ([key, attribute]) =>
      ({
        label: attribute.label,
        key,
        type: "RANGE_FILTER",
        range: [1, 99],
        minValue: null,
        maxValue: null,
      }) as RangeFilterType,
  ),
];

export type PlayerFilter = (RangeFilterType | SelectorFilterType)[];

export const inGameAttributeCategories = [
  "PACE",
  "SHOOTING",
  "PASSING",
  "DRIBBLING",
  "DEFENDING",
  "PHYSICALITY",
  "GOALKEEPING",
] as const;

export type InGameAttributeCategory =
  (typeof inGameAttributeCategories)[number];

export const setEmptyPlayerFilters = ({
  playStylesFilterOptions,
  playStylesPlusFilterOptions,
}: {
  playStylesFilterOptions?: SelectorOption[];
  playStylesPlusFilterOptions?: SelectorOption[];
}) =>
  Array(11).fill(
    setEmptyAttributes({
      playStylesFilterOptions: playStylesFilterOptions || [],
      playStylesPlusFilterOptions: playStylesPlusFilterOptions || [],
    }),
  );

export const setEmptyBench: ({
  playerRoles,
}: {
  playerRoles: { playerRoleId: number; position: string }[];
}) => SquadBuilderPlayerType[] = ({ playerRoles }) => {
  return playerRoles.map((pr) => ({
    ...emptyPlayer,
    playerRoleId: pr.playerRoleId,
    position: pr.position,
  }));
};

type OtherAttributeSetting = {
  label: string;
  options?: (string | number)[];
  exclusive?: boolean;
};

export const otherAttributeSettings: { [key: string]: OtherAttributeSetting } =
  {
    skillMoves: {
      label: "Skill Moves",
      options: [1, 2, 3, 4, 5],
    },
    weakFoot: {
      label: "Weak Foot",
      options: [1, 2, 3, 4, 5],
    },
    preferredFoot: {
      label: "Preferred Foot",
      options: ["Left", "Right"],
      exclusive: true,
    },
    height: { label: "Height" },
  };

export const inGameAttributesDict = {
  pace: { label: "PACE", category: "PACE" },
  dribblingTitle: { label: "DRIBBLING", category: "DRIBBLING" },
  physicality: { label: "PHYSICALITY", category: "PHYSICALITY" },
  defending: { label: "DEFENDING", category: "DEFENDING" },
  shooting: { label: "SHOOTING", category: "SHOOTING" },
  passing: { label: "PASSING", category: "PASSING" },
  acceleration: { label: "Acceleration", category: "PACE" },
  sprintSpeed: { label: "Sprint Speed", category: "PACE" },
  agility: { label: "Agility", category: "DRIBBLING" },
  balance: { label: "Balance", category: "DRIBBLING" },
  jumping: { label: "Jumping", category: "PHYSICALITY" },
  stamina: { label: "Stamina", category: "PHYSICALITY" },
  strength: { label: "Strength", category: "PHYSICALITY" },
  reactions: { label: "Reactions", category: "DRIBBLING" },
  aggression: { label: "Aggression", category: "PHYSICALITY" },
  composure: { label: "Composure", category: "DRIBBLING" },
  interceptions: { label: "Interceptions", category: "DEFENDING" },
  positioning: { label: "Positioning", category: "SHOOTING" },
  vision: { label: "Vision", category: "PASSING" },
  ballControl: { label: "Ball Control", category: "DRIBBLING" },
  crossing: { label: "Crossing", category: "PASSING" },
  dribbling: { label: "Dribbling", category: "DRIBBLING" },
  finishing: { label: "Finishing", category: "SHOOTING" },
  freeKickAccuracy: { label: "FK Accuracy", category: "PASSING" },
  headingAccuracy: { label: "Heading Accuracy", category: "DEFENDING" },
  longPassing: { label: "Long Passing", category: "PASSING" },
  shortPassing: { label: "Short Passing", category: "PASSING" },
  defensiveAwareness: { label: "Def. Awareness", category: "DEFENDING" },
  shotPower: { label: "Shot Power", category: "SHOOTING" },
  longShots: { label: "Long Shots", category: "SHOOTING" },
  standingTackles: { label: "Standing Tackles", category: "DEFENDING" },
  slidingTackles: { label: "Sliding Tackles", category: "DEFENDING" },
  volleys: { label: "Volleys", category: "SHOOTING" },
  curve: { label: "Curve", category: "PASSING" },
  penalties: { label: "Penalties", category: "SHOOTING" },
  gkDiving: { label: "GK Diving", category: "GOALKEEPING" },
  gkHandling: { label: "GK Handling", category: "GOALKEEPING" },
  gkKicking: { label: "GK Kicking", category: "GOALKEEPING" },
  gkReflexes: { label: "GK Reflexes", category: "GOALKEEPING" },
  gkPositioning: { label: "GK Positioning", category: "GOALKEEPING" },
};

export const calculateMetaRatingAndChemistry = (
  players: SquadBuilderPlayerType[],
  manager: Manager,
  formation: string,
  chemistryProfiles: ChemistryProfiles,
) => {
  const chemistry = getChemistry({
    players: players.map((p, index) => {
      return {
        clubId: p.clubId,
        leagueId: p.leagueId,
        countryId: p.countryId,
        positionIndex: index,
        possiblePositions: p.possiblePositions || [],
        versionId: p.versionId,
      };
    }),
    manager,
    formation,
    chemistryProfiles,
  });

  const teamMetaRating = getTeamMetaRating({
    playersChemistry: chemistry.playersChemistry,
    players,
  });
  return { teamMetaRating, chemistry };
};

export const prepareFiltesForAPI = (playerFilters: PlayerFilter[]) =>
  playerFilters.reduce((acc, pf) => {
    const inGameAttributes = pf.reduce((acc, f) => {
      if (
        Object.keys(inGameAttributesDict).includes(f.key) &&
        f.type === "RANGE_FILTER" &&
        (f.minValue || f.maxValue)
      ) {
        acc[f.key] = {
          min: f.minValue,
          max: f.maxValue,
        };
      }
      return acc;
    }, {});
    const otherAttributes = pf.reduce((acc, f) => {
      if (
        !Object.keys(inGameAttributesDict).includes(f.key) &&
        f.type === "SELECTOR_FILTER" &&
        f.selectedOptions.length > 0
      ) {
        acc[f.key] = f.selectedOptions.map((so) => {
          if (f.useLabelAsId) {
            return f.options.find((o) => o.id === so)?.label;
          }
          return f.options.find((o) => o.id === so)?.id;
        });
      }
      return acc;
    }, {});

    if (
      Object.keys(inGameAttributes).length > 0 ||
      Object.keys(otherAttributes).length > 0
    )
      acc.push({
        inGameAttributes,
        otherAttributes,
      });
    else acc.push(null);

    return acc;
  }, [] as any[]);

export const defaultTactic: Tactic = {
  name: "FC Preset",
  description:
    "A direct approach which focuses on breaking down defenses with quick and incisive attacking runs",
  popularityScore: 0,
  eaCode: "PYnQqofWadf",
  logoUrl: "http://cdn.easysbc.io/fc25/tactics/logos/preset2.png",
  author: "EA Sports",
  tags: ["Preset"],
  teamTactics: {
    buildUpStyle: "Balanced",
    formationId: "f433",
    defensiveApproach: "Balanced",
    lineHeight: 50,
  },
  isTrending: false,
  isFavorite: false,
  isNew: false,
  publishedAt: 0,

  playerRoles: [
    { focus: "Balanced", playerRoleId: 1 },
    { focus: "Balanced", playerRoleId: 5 },
    { focus: "Defend", playerRoleId: 11 },
    { focus: "Defend", playerRoleId: 13 },
    { focus: "Balanced", playerRoleId: 7 },
    { focus: "Balanced", playerRoleId: 18 },
    { focus: "Defend", playerRoleId: 19 },
    { focus: "Attack", playerRoleId: 21 },
    { focus: "Balanced", playerRoleId: 36 },
    { focus: "Attack", playerRoleId: 42 },
    { focus: "Balanced", playerRoleId: 38 },
    { focus: "Balanced", playerRoleId: 35 },
    { focus: "Attack", playerRoleId: 41 },
    { focus: "Balanced", playerRoleId: 38 },
    { focus: "Attack", playerRoleId: 21 },
    { focus: "Defend", playerRoleId: 11 },
    { focus: "Balanced", playerRoleId: 3 },
    { focus: "Defend", playerRoleId: 19 },
    { focus: "Balanced", playerRoleId: 35 },
    { focus: "Attack", playerRoleId: 41 },
    { focus: "Balanced", playerRoleId: 38 },
    { focus: "Balanced", playerRoleId: 18 },
    { focus: "Defend", playerRoleId: 11 },
  ],
};

export const BASIC_CHEMSTYLE = 250;
